import React, { useEffect } from "react";
import { useHistory, useLocation } from "react-router-dom";

import { LeerMakeMake } from "./leer-makemake";
import { AprenderMakeMake } from "./aprender-makemake";
import { PlanCasaNav } from "./components/plan-casa-nav";
import { DemoRequest } from "../../components/demo-request";
import { CustomPlan } from "./components/custom-plan";

import { useTranslations } from "../../hooks/use-translations";
import useIsMobile from "../../js/utilities/useIsMobile";
import { useIntersectionScroll } from "../../hooks/use-intersection-scroll";
import { useLanguage } from "../../hooks/use-language";
import { useId } from "../../hooks/use-id";

/**
 * Componente principal del plan Casa. Este reune la información de planes y cursos
 * @returns {JSX.Element} Casa
 */
export const Casa = () => {
	const lang = useLanguage();
	const textosTienda = useTranslations({ section: "tienda_makemake", lang: lang });

	const history = useHistory();
	const location = useLocation();

	const isMobile = useIsMobile();

	const [isIntersectingLeer, leerMakeMake] = useIntersectionScroll({ threshold: 0 });
	const [isIntersectingAprender, aprenderMakeMake] = useIntersectionScroll({ threshold: 0 });

	const tabLeerId = useId("tab-leer");
	const panelLeerId = useId("panel-leer");
	const tabAprenderId = useId("tab-aprender");
	const panelAprenderId = useId("panel-aprender");

	const SECCIONES_PLAN_CASA = [
		{
			id: "leer-makemake",
			tabId: tabLeerId,
			panelId: panelLeerId,
			url: "/tienda/plan-casa/leer",
			title: textosTienda?.tabpanel?.leer || "Leer en makemake",
			ref: leerMakeMake,
		},
		{
			id: "aprender-makemake",
			tabId: tabAprenderId,
			panelId: panelAprenderId,
			url: "/tienda/plan-casa/aprender",
			title: textosTienda?.tabpanel?.aprender || "Aprender algo nuevo",
			ref: aprenderMakeMake,
		},
	];

	useEffect(() => {
		/**
            dependiendo de la ruta del navegador si coincide con alguna url 
            de las secciones, entonces muestra la sección respectiva
        */
		SECCIONES_PLAN_CASA.map((item) => {
			if (location.pathname === item.url) {
				const element = item?.ref.current;

				window.scrollTo({
					top: isMobile ? element?.offsetTop - 160 : element?.offsetTop - 125,
					behavior: "smooth",
				});
			}
		});
	}, [location]);

	const navigateTo = (ref, url) => {
		const link = url ? url : "/tienda/plan-casa";

		if (ref?.current) {
			const element = ref?.current;

			window.scrollTo({
				top: isMobile ? element?.offsetTop - 160 : element?.offsetTop - 125,
				behavior: "smooth",
			});

			setTimeout(() => {
				element.focus();
			}, 300);
		} else {
			history.push(link);
		}
	};

	const handleKeyDown = (e, index) => {
		const tabs = SECCIONES_PLAN_CASA;

		let newIndex = index;

		switch (e.key) {
			case "ArrowRight":
				newIndex = (index + 1) % tabs.length;
				break;
			case "ArrowLeft":
				newIndex = (index - 1 + tabs.length) % tabs.length;
				break;
			case "Home":
				newIndex = 0;
				break;
			case "End":
				newIndex = tabs.length - 1;
				break;
			default:
				return;
		}

		e.preventDefault();
		navigateTo(tabs[newIndex].ref, tabs[newIndex].url);
	};

	return (
		<>
			<PlanCasaNav.tabpanel
				ariaLabel={textosTienda?.sectionLabel?.main || "Secciones del plan casa"}
			>
				{SECCIONES_PLAN_CASA.map((tab, i) => {
					return (
						<PlanCasaNav.tab
							key={tab.id}
							title={tab.title}
							tabId={tab.tabId}
							panelId={tab.panelId}
							active={
								tab.id === "leer-makemake"
									? isIntersectingLeer
									: isIntersectingAprender
							}
							onClick={() => {
								navigateTo(tab.ref, tab.url);
							}}
							onKeyDown={(e) => handleKeyDown(e, i)}
						/>
					);
				})}
				<PlanCasaNav.tab></PlanCasaNav.tab>
			</PlanCasaNav.tabpanel>

			<div className="sr-only" aria-live="polite">
				{(isIntersectingLeer && textosTienda?.sectionLabel?.read_mkmk) ||
					"Sección Leer en MakeMake"}
				{(isIntersectingAprender && textosTienda?.sectionLabel?.learn_mkmk) ||
					"Sección Aprender algo nuevo"}
			</div>

			<div>
				<div className="container">
					<div
						ref={leerMakeMake}
						className="mt-4"
						id={panelLeerId}
						role="tabpanel"
						aria-labelledby={tabLeerId}
						tabIndex={0}
					>
						<LeerMakeMake />
					</div>
				</div>

				<CustomPlan />

				<div className="container">
					<div
						ref={aprenderMakeMake}
						id={panelAprenderId}
						role="tabpanel"
						aria-labelledby={tabAprenderId}
						className="mt-4"
						tabIndex={0}
					>
						<AprenderMakeMake />
					</div>
				</div>

				<DemoRequest />
			</div>
		</>
	);
};
